/**
 * Sprint T-17.1 · Notification Center · ProvidersHealthTab.
 * 5 cards independientes · manual refresh + optional 60s poll toggle.
 * §4.7
 */
import React, { useState } from "react";
import { RefreshCw, Circle } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useProvidersHealth } from "../../hooks/useNotifications";
import ChannelIcon from "./shared/ChannelIcon";

function ProviderCard({ channel, info, testId }) {
  const ok = info?.ok !== false;
  const isNoop = String(info?.provider || "").toLowerCase() === "noop";
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border p-4 ${
        ok ? "border-[var(--ag-border)] bg-[var(--ag-surface)]" : "border-red-500/40 bg-red-500/5"
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChannelIcon channel={channel} size={16} />
          <span className="text-sm font-semibold uppercase tracking-wide">{channel}</span>
        </div>
        {isNoop && (
          <span
            data-testid={`${testId}-noop-badge`}
            className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-500"
          >
            noop
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Circle
          className={`h-2 w-2 ${ok ? "fill-emerald-500 text-emerald-500" : "fill-red-500 text-red-500"}`}
        />
        <span
          data-testid={`${testId}-status`}
          className={`font-mono text-xs ${ok ? "text-emerald-500" : "text-red-500"}`}
        >
          {ok ? "OK" : "FAIL"}
        </span>
      </div>
      <div className="mt-2 space-y-1 text-[10px] text-[var(--ag-text-dim)]">
        <div>
          <span className="uppercase tracking-wide">provider:</span>{" "}
          <span className="font-mono">{info?.provider || "—"}</span>
        </div>
        <div>
          <span className="uppercase tracking-wide">detail:</span>{" "}
          <span className="font-mono" data-testid={`${testId}-detail`}>{info?.detail || "—"}</span>
        </div>
      </div>
    </div>
  );
}

export default function ProvidersHealthTab() {
  const [poll, setPoll] = useState(false);
  const q = useProvidersHealth({ poll: poll ? 60000 : null });

  const providers = q.data?.providers || {};
  const total = Object.keys(providers).length;

  return (
    <section className="space-y-4" data-testid="notif-providers-tab">
      <WidgetCard
        title={`Providers · ${total}`}
        description="Estado de cada adapter · Resend + Webhook activos · SMS/WA/Push como Noop stubs"
        citations={["/api/notifications/providers/health"]}
        testId="notif-providers-list"
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[10px] text-[var(--ag-text-dim)]">
              <input
                type="checkbox"
                data-testid="notif-providers-poll-toggle"
                checked={poll}
                onChange={(e) => setPoll(e.target.checked)}
              />
              auto 60s
            </label>
            <button
              type="button"
              data-testid="notif-providers-refresh"
              onClick={() => q.refetch()}
              className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] hover:bg-[var(--ag-surface-2)]"
            >
              <RefreshCw className={`h-3 w-3 ${q.isFetching ? "animate-spin" : ""}`} /> Refrescar
            </button>
          </div>
        }
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="providers/health no responde" testId="notif-providers-error" />}
        {q.data && total === 0 && <EmptyState testId="notif-providers-empty" />}
        {total > 0 && (
          <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {Object.entries(providers).map(([channel, info]) => (
              <ProviderCard
                key={channel}
                channel={channel}
                info={info}
                testId={`notif-provider-${channel}`}
              />
            ))}
          </WidgetGrid>
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["Provider Adapter Pattern · NotificationProvider ABC"]} />
      </footer>
    </section>
  );
}
